import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../types/types';
import { RestUser } from '../../../services/auth/types/user';
import { getTrips } from '../../../services/trips/api/tripsApi';
import { getSocialSummary, SocialSummary } from '../../../services/social/api';
import { getMyPosts, deletePost, ServerPost } from '../../../services/posts/api';
import DashboardLayout from '../../layouts/DashboardLayout';
import FeedPostCard from '../../components/feed/FeedPostCard';
import { toFeedPost } from '../../components/feed/toFeedPost';
import { ProfileTripPreview, getVisibleTripTags, mapTripToProfilePreview } from '../../utils/mapTripToProfilePreview';
import EmptyState from '../../components/common/EmptyState';
import './ProfilePage.css';

interface ProfilePageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop stop-color="#0d4f3c"/>
      <stop offset="1" stop-color="#d9ff31"/>
    </linearGradient>
  </defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>
`)}`;

const formatPostTime = (createdAt: string): string => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' · '
    + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const ProfilePage: React.FC<ProfilePageProps> = ({ user, restUser }) => {
  const navigate = useNavigate();
  const tripsScrollerRef = React.useRef<HTMLDivElement>(null);
  const [profileTrips, setProfileTrips] = React.useState<ProfileTripPreview[]>([]);
  const [isLoadingTrips, setIsLoadingTrips] = React.useState(true);
  const [tripsError, setTripsError] = React.useState<string | null>(null);
  const [social, setSocial] = React.useState<SocialSummary>({
    followers: 0,
    following: 0,
    isFollowing: false,
    isSelf: true,
  });
  const [myPosts, setMyPosts] = React.useState<ServerPost[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = React.useState(true);
  const [postsError, setPostsError] = React.useState<string | null>(null);
  const profileName = user?.name && !user.name.includes('@') ? user.name : '';
  const displayName = [restUser?.firstName, restUser?.lastName].filter(Boolean).join(' ') || profileName || 'Emma Wilson';
  const avatar = restUser?.picture_url || restUser?.avatarUrl || user?.avatar || fallbackAvatar;
  const joinedYear = restUser?.createdAt ? new Date(restUser.createdAt).getFullYear() : '2023';
  const profileHandle = restUser?.nickname || restUser?.email || user?.email || 'traveler';
  const isCurrentUser = true;
  const hasMoreTrips = profileTrips.length > 3;

  const fetchProfileTrips = React.useCallback(async () => {
    try {
      setIsLoadingTrips(true);
      setTripsError(null);
      // A preview strip on the profile, not the archive — the first page IS
      // the right amount, and "see all" goes to /profile/trips.
      const { trips } = await getTrips();
      setProfileTrips(trips.map((trip, index) => mapTripToProfilePreview(trip, index)));
    } catch (err: any) {
      setTripsError(err.message || 'Failed to load profile trips.');
    } finally {
      setIsLoadingTrips(false);
    }
  }, []);

  React.useEffect(() => {
    fetchProfileTrips();
  }, [fetchProfileTrips]);

  // Real follower/following counts for the signed-in user.
  React.useEffect(() => {
    const userId = restUser?.id;
    if (!userId) return undefined;
    let cancelled = false;
    getSocialSummary(userId)
      .then((summary) => {
        if (!cancelled) setSocial(summary);
      })
      .catch(() => {
        if (!cancelled) {
          setSocial({ followers: 0, following: 0, isFollowing: false, isSelf: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restUser?.id]);

  // My posts (T2.10a) — real data for the Posts section below.
  React.useEffect(() => {
    let cancelled = false;
    setIsLoadingPosts(true);
    setPostsError(null);
    getMyPosts()
      .then((page) => {
        if (!cancelled) setMyPosts(page.posts);
      })
      .catch((err: any) => {
        if (!cancelled) setPostsError(err?.message || 'Failed to load posts.');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPosts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDeletePost = React.useCallback(async (postId: string) => {
    const previous = myPosts;
    setMyPosts((current) => current.filter((post) => post.postId !== postId));
    try {
      await deletePost(postId);
    } catch (err: any) {
      setMyPosts(previous); // restore — the server still has it
      setPostsError(err?.message || 'Failed to delete the post.');
    }
  }, [myPosts]);

  const scrollTrips = (direction: 'previous' | 'next') => {
    tripsScrollerRef.current?.scrollBy({
      left: direction === 'next' ? 216 : -216,
      behavior: 'smooth',
    });
  };

  const openTrip = (selectedTrip: ProfileTripPreview) => {
    navigate(`/trips/${selectedTrip.id}`, {
      state: { backTo: '/profile', owned: isCurrentUser, trip: selectedTrip.sourceTrip },
    });
  };

  return (
    <DashboardLayout activeSection="settings" wide>
      <div className="v2-profile-page">
        <section className="v2-profile-hero" aria-label={`${displayName} profile`}>
          <div className="v2-profile-hero__cover" />
          <div className="v2-profile-hero__content">
            <img
              className="v2-profile-hero__avatar"
              src={avatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
            <div className="v2-profile-hero__details">
              <div className="v2-profile-hero__title-row">
                <div>
                  <h1>{displayName}</h1>
                  <p>{profileHandle} · Joined {joinedYear}</p>
                </div>
                {!isCurrentUser && (
                  <div className="v2-profile-hero__actions">
                    <button type="button" className="v2-profile-hero__follow">+ Follow</button>
                    <button type="button" className="v2-profile-hero__message">Message</button>
                  </div>
                )}
              </div>
              <p className="v2-profile-hero__bio">
                Chasing sunsets, great food, and memorable routes. Planning and sharing trips with Wayplume.
              </p>
            </div>
          </div>
        </section>

        <section className="v2-profile-stats" aria-label="Profile stats">
          <ProfileStat value={String(myPosts.length)} label="Posts" />
          <ProfileStat
            value={social.followers.toLocaleString()}
            label="Followers"
            badge={social.pendingRequests || 0}
            onClick={() => navigate('/connections?tab=followers')}
          />
          <ProfileStat
            value={social.following.toLocaleString()}
            label="Following"
            onClick={() => navigate('/connections?tab=following')}
          />
          <ProfileStat value={isLoadingTrips ? '...' : String(profileTrips.length)} label="Trips" />
        </section>

        <section className="v2-profile-section" aria-labelledby="profile-trips-heading">
          <div className="v2-profile-section__header">
            <h2 id="profile-trips-heading">Trips <span>· {profileTrips.length}</span></h2>
            <div className="v2-profile-section__actions">
              <button
                type="button"
                className="v2-profile-section__see-all"
                onClick={() => navigate('/bookmarks')}
              >
                Saved
              </button>
              {hasMoreTrips && (
                <button
                  type="button"
                  className="v2-profile-section__see-all"
                  onClick={() => navigate('/profile/trips')}
                >
                  See all trips
                </button>
              )}
              <button type="button" aria-label="Previous trips" onClick={() => scrollTrips('previous')}>‹</button>
              <button type="button" aria-label="Next trips" onClick={() => scrollTrips('next')}>›</button>
            </div>
          </div>
          {isLoadingTrips && <ProfileTripsState message="Loading trips..." />}
          {!isLoadingTrips && tripsError && (
            <ProfileTripsState message={tripsError} actionLabel="Retry" onAction={fetchProfileTrips} />
          )}
          {!isLoadingTrips && !tripsError && profileTrips.length === 0 && (
            <EmptyState
              compact
              title="No trips yet"
              body="Trips you plan show up here. Start one with the AI and it drafts the itinerary for you."
              action={{ label: 'Plan a trip', to: '/ask-ai' }}
            />
          )}
          {!isLoadingTrips && !tripsError && profileTrips.length > 0 && (
            <div className="v2-profile-trips" ref={tripsScrollerRef}>
              {profileTrips.map((trip) => {
                const { visible: visibleTags, hasMore } = getVisibleTripTags(trip.tags);

                return (
                <button
                  type="button"
                  key={trip.id}
                  className="v2-profile-trip-card"
                  onClick={() => openTrip(trip)}
                >
                  <img src={trip.imageUrl} alt="" />
                  <div className="v2-profile-trip-card__content">
                    <h3>{trip.title}</h3>
                    {visibleTags.length > 0 && (
                      <div className="v2-profile-trip-card__tags" aria-label={`${trip.title} tags`}>
                        {visibleTags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                        {hasMore && <span className="v2-profile-trip-card__tags-more">more</span>}
                      </div>
                    )}
                    <p>{trip.meta}</p>
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="v2-profile-section v2-profile-section--posts" aria-labelledby="profile-posts-heading">
          <h2 id="profile-posts-heading">Posts</h2>
          {postsError && <p className="v2-profile-posts__notice">{postsError}</p>}
          {isLoadingPosts && <p className="v2-profile-posts__notice">Loading your posts…</p>}
          {!isLoadingPosts && !postsError && myPosts.length === 0 && (
            <EmptyState
              compact
              title="No posts yet"
              body="Posts are how your trips reach other travellers. Share a photo or a note from the Feed and it lands here."
              action={{ label: 'Write a post', to: '/feed' }}
            />
          )}
          {/*
            THE SAME CARD THE FEED USES, not a read-only lookalike. This block
            drew its own <article> with no engagement row at all, so a post you
            had just liked in the feed showed nothing here. The server has
            always enriched `/posts/mine`; only the render threw it away.
            Delete moves onto the card's own ⋯ menu via `onDelete`.
          */}
          <div className="v2-profile-posts">
            {myPosts.map((post) => (
              <FeedPostCard
                key={post.postId}
                post={toFeedPost(post, { name: displayName, avatarUrl: avatar })}
                currentUserId={restUser?.id}
                onDelete={handleDeletePost}
              />
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

interface ProfileStatProps {
  value: string;
  label: string;
  /** Pending-request count — renders as a circle at the top-right (D-35). */
  badge?: number;
  onClick?: () => void;
}

const ProfileStat: React.FC<ProfileStatProps> = ({ value, label, badge = 0, onClick }) => {
  const content = (
    <>
      {badge > 0 && (
        <span className="v2-profile-stats__badge" aria-label={`${badge} pending follow requests`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      <strong>{value}</strong>
      <span>{label}</span>
    </>
  );
  if (!onClick) return <div className="v2-profile-stats__item">{content}</div>;
  return (
    <button type="button" className="v2-profile-stats__item v2-profile-stats__item--link" onClick={onClick}>
      {content}
    </button>
  );
};

interface ProfileTripsStateProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const ProfileTripsState: React.FC<ProfileTripsStateProps> = ({ message, actionLabel, onAction }) => (
  <div className="v2-profile-trips-state">
    <p>{message}</p>
    {actionLabel && onAction && (
      <button type="button" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

export default ProfilePage;
